// RT-21: TOTP 2FA enrollment component.
// Uses Supabase MFA enroll/challengeAndVerify flow.

import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Spinner } from '../ui/Spinner';

type SetupStep = 'idle' | 'loading' | 'qr' | 'verify' | 'done' | 'disable';

interface FactorInfo {
  id: string;
  status: 'verified' | 'unverified';
}

export function TwoFactorSetup() {
  const [step,        setStep]        = useState<SetupStep>('idle');
  const [qrUrl,       setQrUrl]       = useState('');
  const [secret,      setSecret]      = useState('');
  const [factorId,    setFactorId]    = useState('');
  const [code,        setCode]        = useState('');
  const [error,       setError]       = useState<string | null>(null);
  const [enrolled,    setEnrolled]    = useState<FactorInfo | null>(null);

  // Check current 2FA status on mount
  useEffect(() => {
    supabase.auth.mfa.listFactors().then(({ data }) => {
      // Support both `totp[]` (old) and `all[]` (new) factor shapes
      const factors = (data as { all?: { id: string; factor_type: string; status: string }[]; totp?: { id: string; status: string }[] } | null);
      const verified = factors?.totp?.find(f => f.status === 'verified')
        ?? factors?.all?.find(f => f.factor_type === 'totp' && f.status === 'verified');
      if (verified) setEnrolled({ id: verified.id, status: 'verified' });
    });
  }, []);

  async function startEnrollment() {
    setStep('loading');
    setError(null);
    const { data, error: enrollErr } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      issuer:     'Professya',
    });
    if (enrollErr || !data) {
      setError('Erro ao iniciar configuração do 2FA.');
      setStep('idle');
      return;
    }
    setFactorId(data.id);
    setQrUrl(data.totp.qr_code);
    setSecret(data.totp.secret);
    setStep('qr');
  }

  async function verifyEnrollment(e: React.FormEvent) {
    e.preventDefault();
    const digits = code.replace(/\D/g, '');
    if (digits.length !== 6) {
      setError('Digite os 6 dígitos do seu aplicativo autenticador.');
      return;
    }

    setStep('loading');
    setError(null);

    const { data: challenge, error: challengeErr } = await supabase.auth.mfa.challenge({ factorId });
    if (challengeErr || !challenge) {
      setError('Erro ao criar desafio.');
      setStep('qr');
      return;
    }

    const { error: verifyErr } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.id,
      code: digits,
    });

    if (verifyErr) {
      setError('Código inválido. Certifique-se de ter escaneado o QR code corretamente.');
      setStep('qr');
      return;
    }

    // Sync totp_enabled flag in profiles so the login flow can redirect quickly
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase.from('profiles').update({ totp_enabled: true }).eq('id', user.id);
    }

    setEnrolled({ id: factorId, status: 'verified' });
    setStep('done');
  }

  async function disableMFA() {
    if (!enrolled) return;
    setStep('loading');
    const { error: unenrollErr } = await supabase.auth.mfa.unenroll({ factorId: enrolled.id });
    if (unenrollErr) {
      setError('Erro ao desativar 2FA. Tente novamente.');
      setStep('idle');
      return;
    }
    // Sync totp_enabled flag, cleared after unenrollment
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase.from('profiles').update({ totp_enabled: false }).eq('id', user.id);
    }
    setEnrolled(null);
    setStep('idle');
  }

  // ── Already enrolled ────────────────────────────────────────────────────────
  if (enrolled) {
    return (
      <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-card)', padding: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <span style={{ fontSize: '1.25rem' }}>🔐</span>
          <div>
            <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: 0, fontSize: 'var(--text-label)' }}>
              Autenticação em dois fatores
            </p>
            <p style={{ color: '#4ade80', margin: '0.1rem 0 0', fontSize: 'var(--text-caption)' }}>
              ✓ Ativa
            </p>
          </div>
        </div>
        {error && <p style={{ color: '#fca5a5', fontSize: 'var(--text-caption)', marginBottom: '0.75rem' }}>{error}</p>}
        <button
          className="btn btn-secondary btn-sm"
          onClick={disableMFA}
          disabled={step === 'loading'}
        >
          {step === 'loading' ? <Spinner size="sm" /> : 'Desativar 2FA'}
        </button>
      </div>
    );
  }

  // ── QR code step ────────────────────────────────────────────────────────────
  if (step === 'qr') {
    return (
      <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-card)', padding: '1.25rem' }}>
        <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem', fontSize: 'var(--text-label)' }}>
          🔐 Configurar 2FA, Passo 1 de 2
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', marginBottom: '1rem', lineHeight: 1.6 }}>
          Escaneie o QR code com Google Authenticator, Authy ou similar:
        </p>
        {qrUrl && (
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <img src={qrUrl} alt="QR code para 2FA" style={{ width: 200, height: 200, borderRadius: 'var(--radius-md)', background: '#fff', padding: '0.5rem' }} />
          </div>
        )}
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', marginBottom: '1rem' }}>
          Ou use o código manual: <code style={{ letterSpacing: '0.1em', color: 'var(--petrol)' }}>{secret}</code>
        </p>
        <form onSubmit={verifyEnrollment}>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.5rem', fontSize: 'var(--text-label)' }}>
            Passo 2: Digite o código gerado pelo app para confirmar
          </p>
          {error && <p style={{ color: '#fca5a5', fontSize: 'var(--text-caption)', marginBottom: '0.5rem' }}>{error}</p>}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              className="form-input"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              style={{ letterSpacing: '0.25em', textAlign: 'center', flex: 1 }}
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={code.replace(/\D/g, '').length !== 6}
            >
              Confirmar
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ── Done ────────────────────────────────────────────────────────────────────
  if (step === 'done') {
    return (
      <div style={{ border: '1px solid rgba(74,222,128,0.3)', borderRadius: 'var(--radius-card)', padding: '1.25rem', backgroundColor: 'rgba(74,222,128,0.06)' }}>
        <p style={{ fontWeight: 600, color: '#4ade80', margin: '0 0 0.25rem', fontSize: 'var(--text-label)' }}>
          ✓ 2FA ativado com sucesso!
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', margin: 0, lineHeight: 1.6 }}>
          A partir do próximo login, você precisará do código do seu aplicativo autenticador.
        </p>
      </div>
    );
  }

  // ── Idle / loading ──────────────────────────────────────────────────────────
  return (
    <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-card)', padding: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <span style={{ fontSize: '1.25rem' }}>🔐</span>
        <div>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: 0, fontSize: 'var(--text-label)' }}>
            Autenticação em dois fatores (2FA)
          </p>
          <p style={{ color: 'var(--text-muted)', margin: '0.1rem 0 0', fontSize: 'var(--text-caption)' }}>
            Adicione uma camada extra de segurança à sua conta.
          </p>
        </div>
      </div>
      <button
        className="btn btn-secondary btn-sm"
        onClick={startEnrollment}
        disabled={step === 'loading'}
      >
        {step === 'loading' ? <Spinner size="sm" /> : 'Ativar 2FA'}
      </button>
    </div>
  );
}
